import { useState } from "react";
import { Button, Card, Form, Input, message, Tabs, Typography } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/stores/auth";
import { authService } from "@/services/auth";

const { Title } = Typography;

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setTokens, setUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("login");

  const handleLogin = async (values: { account: string; password: string }) => {
    setLoading(true);
    try {
      const result = await authService.login(values.account, values.password);
      setTokens(result.tokens.access_token, result.tokens.refresh_token);
      setUser(result.user);
      navigate("/management", { replace: true });
    } catch (e) {
      message.error(e instanceof Error ? e.message : t("error"));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values: { name: string; account: string; password: string }) => {
    setLoading(true);
    try {
      await authService.register(values.name, values.account, values.password);
      message.success(t("success"));
      setTab("login");
    } catch (e) {
      message.error(e instanceof Error ? e.message : t("error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      height: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "var(--content-bg, #F5F7FA)",
    }}>
      <Card style={{ width: 380 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <img src="/brand-128.png" alt="logo" style={{ width: 64, height: 64, marginBottom: 12 }} />
          <Title level={4} style={{ margin: 0 }}>{t("app.name")}</Title>
        </div>

        <Tabs activeKey={tab} onChange={setTab} centered items={[
          {
            key: "login",
            label: t("login.title"),
            children: (
              <Form onFinish={handleLogin} autoComplete="off" size="large">
                <Form.Item name="account" rules={[{ required: true }]}>
                  <Input prefix={<UserOutlined />} placeholder={t("login.account")} />
                </Form.Item>
                <Form.Item name="password" rules={[{ required: true }]}>
                  <Input.Password prefix={<LockOutlined />} placeholder={t("login.password")} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" block loading={loading}>
                    {t("login.submit")}
                  </Button>
                </Form.Item>
              </Form>
            ),
          },
          {
            key: "register",
            label: t("login.register.title"),
            children: (
              <Form onFinish={handleRegister} autoComplete="off" size="large">
                <Form.Item name="name" rules={[{ required: true }]}>
                  <Input prefix={<UserOutlined />} placeholder={t("login.register.name")} />
                </Form.Item>
                <Form.Item name="account" rules={[{ required: true }]}>
                  <Input prefix={<UserOutlined />} placeholder={t("login.account")} />
                </Form.Item>
                <Form.Item name="password" rules={[{ required: true, min: 6 }]}>
                  <Input.Password prefix={<LockOutlined />} placeholder={t("login.password")} />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" block loading={loading}>
                    {t("login.register.submit")}
                  </Button>
                </Form.Item>
              </Form>
            ),
          },
        ]} />
      </Card>
    </div>
  );
}
